import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Paper,
  Select,
  Typography,
} from "@mui/material";
import {
  BOOKS_ZH,
  getCanonicalChapterCount,
} from "../../bible/bookNames";
import { toPositiveVerse } from "../../bible/readerVerseFocus";
import { cleanVerseQuoteText, formatVerseQuote } from "../../bible/verseQuote";

export const verseSelectOptions = (verseNums, verse) => {
  const nums = (Array.isArray(verseNums) ? verseNums : [])
    .map(Number)
    .filter((num) => Number.isFinite(num) && num > 0);
  const current = toPositiveVerse(verse);
  if (current && !nums.includes(current)) {
    nums.push(current);
    nums.sort((a, b) => a - b);
  }
  return nums.length ? nums : [1];
};

const nativeSelectSx = {
  bgcolor: "background.paper",
  "& .MuiNativeSelect-select": {
    py: 1.15,
    fontWeight: 700,
  },
};

export default function QuotePickerDialog({
  open,
  draft,
  onClose,
  onConfirm,
  onBookChange,
  onChapterChange,
  onVerseChange,
}) {
  if (!open || !draft) return null;

  const chapterOptions = Array.from(
    { length: getCanonicalChapterCount(draft.bookIndex) },
    (_, index) => index + 1
  );
  const verseOptions = verseSelectOptions(draft.verseNums, draft.verse);

  return (
    <Dialog
      open
      onClose={() => {
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
        onClose();
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>确认引用经文</DialogTitle>
      <DialogContent sx={{ pt: 1.5 }} data-testid="quote-picker-dialog">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          选择书卷、章、节，预览会随选择更新。
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr 1fr" },
            gap: 1.25,
            mb: 2,
          }}
        >
          <FormControl fullWidth size="small">
            <InputLabel htmlFor="quote-book-select" shrink>
              书卷
            </InputLabel>
            <Select
              native
              notched
              label="书卷"
              value={draft.bookIndex || ""}
              onChange={(e) => onBookChange(Number(e.target.value))}
              inputProps={{
                id: "quote-book-select",
                "data-testid": "quote-book-select",
                "aria-label": "书卷",
              }}
              sx={nativeSelectSx}
            >
              {BOOKS_ZH.map((name, index) => (
                <option key={name} value={index + 1}>
                  {name}
                </option>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel htmlFor="quote-chapter-select" shrink>
              章
            </InputLabel>
            <Select
              native
              notched
              label="章"
              value={draft.chapterNumber || 1}
              onChange={(e) => onChapterChange(Number(e.target.value))}
              inputProps={{
                id: "quote-chapter-select",
                "data-testid": "quote-chapter-select",
                "aria-label": "章",
              }}
              sx={nativeSelectSx}
            >
              {chapterOptions.map((num) => (
                <option key={num} value={num}>
                  第 {num} 章
                </option>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel htmlFor="quote-verse-select" shrink>
              选择节数
            </InputLabel>
            <Select
              native
              notched
              label="选择节数"
              value={draft.verse}
              onChange={(e) => onVerseChange(Number(e.target.value) || 1)}
              inputProps={{
                id: "quote-verse-select",
                "data-testid": "quote-verse-select",
                "aria-label": "选择节数",
              }}
              sx={nativeSelectSx}
            >
              {verseOptions.map((num) => (
                <option key={num} value={num}>
                  第 {num} 节
                </option>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Paper
          elevation={0}
          data-testid="quote-picker-preview"
          sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: (theme) =>
              theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "#f8fafc",
            borderLeft: "3px solid",
            borderColor: "primary.main",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
            {formatVerseQuote({
              bookName: draft.bookName,
              chapterNumber: draft.chapterNumber,
              verse: draft.verse,
              verseText: "",
            })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {cleanVerseQuoteText(draft.verseText) || "暂无本节经文正文，仍可引用出处。"}
          </Typography>
        </Paper>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          取消
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          sx={{ textTransform: "none", fontWeight: 800 }}
        >
          确认引用
        </Button>
      </DialogActions>
    </Dialog>
  );
}
